import type { ArticleData } from "@qendo/resources";
import type { ListData } from "@qendo/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { useApi } from "~/providers/ApiProvider";

declare interface ResourcesState {
    blogs: ArticleData[];
    refreshBlog(): Promise<void>;
}

export const ResourcesContext = createContext<ResourcesState>({
    blogs: [],
    async refreshBlog() {
        return;
    },
});

export function useResources() {
    return useContext(ResourcesContext);
}

export function ResourcesProvider({ children }: { children: ReactNode }) {
    const { client } = useApi();
    const queryClient = useQueryClient();

    const blogsQuery = useQuery({
        queryKey: ["blogs"],
        queryFn: async () => {
            return await client()
                .get("blog")
                .then((response: AxiosResponse<ListData<ArticleData>>) => [...response.data.data])
                .catch(() => []);
        },
        staleTime: 1000 * 60 * 60 * 3, // 3 hours
        placeholderData: [],
    });

    const refreshBlog = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ["blogs"] });
    }, [queryClient]);

    const value = {
        blogs: blogsQuery.data ?? [],
        refreshBlog,
    };

    return <ResourcesContext.Provider value={value}>{children}</ResourcesContext.Provider>;
}
